/**
 * Pilot Journal
 * 
 * Captures operational observations during the pilot — not bug reports, but
 * insights about how the agency operates inside Leynix.
 * 
 * Observation Categories:
 * - Workflow Improvement
 * - User Experience
 * - Automation Opportunity
 * - Bug / Error
 * - Performance
 * - Training / Documentation
 * - Future Enhancement
 * 
 * Every observation becomes part of the Version 1.01 refinement process.
 */
import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import {
  BookOpen, Plus, Filter, ChevronDown, ChevronUp, Clock,
  Loader2, CheckCircle, AlertCircle, Sparkles, MessageSquare,
  FileText, Zap, Bug, Gauge, GraduationCap, Lightbulb, X, Send
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

// Category configurations
const CATEGORY_CONFIG = {
  workflow_improvement: { icon: Zap, color: "bg-blue-100 text-blue-700", label: "Workflow Improvement" },
  user_experience: { icon: Sparkles, color: "bg-purple-100 text-purple-700", label: "User Experience" },
  automation_opportunity: { icon: Lightbulb, color: "bg-amber-100 text-amber-700", label: "Automation Opportunity" },
  bug_error: { icon: Bug, color: "bg-red-100 text-red-700", label: "Bug / Error" },
  performance: { icon: Gauge, color: "bg-emerald-100 text-emerald-700", label: "Performance" },
  training_documentation: { icon: GraduationCap, color: "bg-indigo-100 text-indigo-700", label: "Training / Documentation" },
  future_enhancement: { icon: FileText, color: "bg-gray-100 text-gray-700", label: "Future Enhancement" },
};

// Status configurations
const STATUS_CONFIG = {
  new: { color: "bg-blue-100 text-blue-700", label: "New" },
  under_review: { color: "bg-amber-100 text-amber-700", label: "Under Review" },
  planned: { color: "bg-purple-100 text-purple-700", label: "Planned" },
  in_progress: { color: "bg-emerald-100 text-emerald-700", label: "In Progress" },
  completed: { color: "bg-gray-100 text-gray-600", label: "Completed" },
  deferred: { color: "bg-gray-100 text-gray-500", label: "Deferred" },
};

// Priority configurations  
const PRIORITY_CONFIG = {
  critical: { color: "bg-red-500 text-white", label: "Critical" },
  high: { color: "bg-orange-500 text-white", label: "High" },
  medium: { color: "bg-amber-500 text-white", label: "Medium" },
  low: { color: "bg-gray-400 text-white", label: "Low" },
};

// Portal options
const PORTAL_OPTIONS = [
  { value: "executive", label: "Executive Portal" },
  { value: "operations", label: "Operations Portal" },
  { value: "hr", label: "HR/Compliance Portal" },
  { value: "finance", label: "Finance Portal" },
  { value: "my_aligned", label: "My Leynix (Caregiver)" },
];

// Observation card
const ObservationCard = ({ observation, onStatusChange }) => {
  const [expanded, setExpanded] = useState(false);
  const categoryConfig = CATEGORY_CONFIG[observation.category] || CATEGORY_CONFIG.future_enhancement;
  const statusConfig = STATUS_CONFIG[observation.status] || STATUS_CONFIG.new;
  const priorityConfig = PRIORITY_CONFIG[observation.priority] || PRIORITY_CONFIG.medium;
  const CategoryIcon = categoryConfig.icon;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryConfig.color}`}>
              <CategoryIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className={categoryConfig.color + " text-xs"}>{categoryConfig.label}</Badge>
                <Badge className={statusConfig.color + " text-xs"}>{statusConfig.label}</Badge>
                <Badge className={priorityConfig.color + " text-xs"}>{priorityConfig.label}</Badge>
              </div>
              <p className="font-medium text-gray-900">{observation.what_happened}</p>
              <p className="text-sm text-gray-500 mt-1">
                {observation.user_name} · {observation.portal} · {new Date(observation.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
          {observation.what_worked_well && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                What Worked Well
              </h4>
              <p className="text-sm text-gray-600 mt-1">{observation.what_worked_well}</p>
            </div>
          )}
          
          {observation.what_was_confusing && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                What Was Confusing
              </h4>
              <p className="text-sm text-gray-600 mt-1">{observation.what_was_confusing}</p>
            </div>
          )}
          
          {observation.what_took_longer && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-500" />
                What Took Longer Than Expected
              </h4>
              <p className="text-sm text-gray-600 mt-1">{observation.what_took_longer}</p>
            </div>
          )}
          
          {observation.what_expected_automatic && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-500" />
                Expected Leynix to Do Automatically
              </h4>
              <p className="text-sm text-gray-600 mt-1">{observation.what_expected_automatic}</p>
            </div>
          )}
          
          {observation.suggested_improvement && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-blue-500" />
                Suggested Improvement
              </h4>
              <p className="text-sm text-gray-600 mt-1">{observation.suggested_improvement}</p>
            </div>
          )}

          {observation.target_version && (
            <div className="pt-2 border-t border-gray-100">
              <Badge className="bg-[#06372B] text-white">
                Planned for Version {observation.target_version}
              </Badge>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// New observation modal
const NewObservationModal = ({ onClose, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    category: "workflow_improvement",
    portal: "operations",
    module: "",
    what_happened: "",
    what_worked_well: "",
    what_was_confusing: "",
    what_took_longer: "",
    what_expected_automatic: "",
    suggested_improvement: "",
    priority: "medium",
  });

  const handleSubmit = async () => {
    if (!form.what_happened.trim()) {
      toast.error("Please describe what happened");
      return;
    }

    setLoading(true);
    try {
      await onSubmit(form);
      onClose();
    } catch (err) {
      toast.error("Failed to submit observation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">New Pilot Observation</h2>
            <p className="text-sm text-gray-500">Share your operational insights</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Category & Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Portal & Module */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Portal</label>
              <Select value={form.portal} onValueChange={(v) => setForm({...form, portal: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PORTAL_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Module (optional)</label>
              <Input
                value={form.module}
                onChange={(e) => setForm({...form, module: e.target.value})}
                placeholder="e.g., Scheduling, Payroll"
              />
            </div>
          </div>

          {/* What Happened */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              What happened? <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.what_happened}
              onChange={(e) => setForm({...form, what_happened: e.target.value})}
              placeholder="Describe the situation or task you were performing..."
              className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#06372B] focus:border-transparent"
              rows={3}
            />
          </div>

          {/* Questions */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <h3 className="font-medium text-gray-900">Reflection Questions</h3>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">What worked well?</label>
              <textarea
                value={form.what_worked_well}
                onChange={(e) => setForm({...form, what_worked_well: e.target.value})}
                className="w-full border border-gray-200 rounded-lg p-2 text-sm"
                rows={2}
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">What was confusing?</label>
              <textarea
                value={form.what_was_confusing}
                onChange={(e) => setForm({...form, what_was_confusing: e.target.value})}
                className="w-full border border-gray-200 rounded-lg p-2 text-sm"
                rows={2}
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">What took longer than expected?</label>
              <textarea
                value={form.what_took_longer}
                onChange={(e) => setForm({...form, what_took_longer: e.target.value})}
                className="w-full border border-gray-200 rounded-lg p-2 text-sm"
                rows={2}
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">What did you expect Leynix to do automatically?</label>
              <textarea
                value={form.what_expected_automatic}
                onChange={(e) => setForm({...form, what_expected_automatic: e.target.value})}
                className="w-full border border-gray-200 rounded-lg p-2 text-sm"
                rows={2}
              />
            </div>
          </div>

          {/* Suggested Improvement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Suggested Improvement</label>
            <textarea
              value={form.suggested_improvement}
              onChange={(e) => setForm({...form, suggested_improvement: e.target.value})}
              placeholder="How could Leynix handle this better?"
              className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#06372B] focus:border-transparent"
              rows={3}
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-[#06372B] hover:bg-[#0a4a3a]"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Submit Observation
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main component
export default function PilotJournal() {
  const [loading, setLoading] = useState(true);
  const [observations, setObservations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    loadData();
  }, [categoryFilter, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);

      const [obsRes, summaryRes] = await Promise.all([
        api.get(`/pilot/journal?${params}`),
        api.get("/pilot/journal/summary"),
      ]);
      
      setObservations(obsRes.data.observations);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error("Failed to load journal:", err);
      toast.error("Failed to load pilot journal");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (form) => {
    await api.post("/pilot/journal", form);
    toast.success("Observation submitted");
    loadData();
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#06372B]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="pilot-journal">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Pilot Journal</h1>
              <p className="text-gray-500">Operational observations for Version 1.01</p>
            </div>
          </div>
          <Button
            onClick={() => setShowNewModal(true)}
            className="bg-[#06372B] hover:bg-[#0a4a3a]"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Observation
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
            <p className="text-sm text-gray-500">Total Observations</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-2xl font-bold text-blue-600">{summary.by_status?.new || 0}</p>
            <p className="text-sm text-gray-500">New</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-2xl font-bold text-amber-600">{summary.by_status?.under_review || 0}</p>
            <p className="text-sm text-gray-500">Under Review</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-2xl font-bold text-emerald-600">{summary.by_status?.completed || 0}</p>
            <p className="text-sm text-gray-500">Completed</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Observations List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#06372B]" />
        </div>
      ) : observations.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Observations Yet</h3>
          <p className="text-gray-500 mb-4">
            Start documenting your pilot experience
          </p>
          <Button onClick={() => setShowNewModal(true)} className="bg-[#06372B] hover:bg-[#0a4a3a]">
            <Plus className="w-4 h-4 mr-2" />
            Submit First Observation
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {observations.map((obs) => (
            <ObservationCard key={obs.id} observation={obs} />
          ))}
        </div>
      )}

      {/* New Observation Modal */}
      {showNewModal && (
        <NewObservationModal
          onClose={() => setShowNewModal(false)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
